import { DestroyRef } from '@angular/core';
import { debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ConnectOptions = {
  onRestore?: (restored: Record<string, unknown>) => void;
  debounceMs?: number;
};

export class ListFilterState {
  private static storage: Storage = sessionStorage;

  static buildKey(routePath: string, componentName?: string): string {
    const base = routePath.split('?')[0];
    const suffix = componentName ? `:${componentName}` : '';
    return `filters:${base}${suffix}`;
  }

  static connect<T extends Record<string, unknown>>(key: string, destroyRef: DestroyRef, options?: ConnectOptions): T | null {
    const opts: Required<ConnectOptions> = {
      onRestore: () => {},
      debounceMs: 300,
      ...options,
    };

    const initial = this.loadJSON<T | null>(key, null);
    if (initial != null) {
      opts.onRestore(initial as Record<string, unknown>);
    }
    return initial;
  }

  static snapshot<T>(key: string, value: T): void {
    this.saveJSON(key, value);
  }

  static restore<T>(key: string): T | null {
    return this.loadJSON<T | null>(key, null);
  }

  static clear(key: string): void {
    this.clearKey(key);
  }

  static setStorage(storage: Storage): void {
    this.storage = storage;
  }

  private static saveJSON(key: string, value: unknown): void {
    try {
      this.storage.setItem(key, JSON.stringify(value, this.dateReplacer));
    } catch {}
  }

  private static loadJSON<T>(key: string, fallback: T): T {
    try {
      const raw = this.storage.getItem(key);
      if (!raw) {
        return fallback;
      }
      return JSON.parse(raw, this.dateReviver) as T;
    } catch {
      return fallback;
    }
  }

  private static clearKey(key: string): void {
    this.storage.removeItem(key);
  }

  private static dateReplacer(_k: string, v: unknown): unknown {
    if (v instanceof Date) {
      return v.toISOString();
    }
    return v;
  }

  private static dateReviver(k: string, v: unknown): unknown {
    if (typeof v === 'string') {
      const looksLikeDateKey = /date|at|on|from|to|time/i.test(k);
      const isoLike = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
      if (looksLikeDateKey && isoLike.test(v)) {
        return new Date(v);
      }
    }
    return v;
  }
}
